import type {
  GridDataSource,
  GridDataSourceCache,
  GridGetRowsParams,
  GridGetRowsResponse,
} from "@mui/x-data-grid-premium";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  TabularDataResponse,
  TabularRow,
  TabularSnapshot,
  TabularSnapshotPageRequest,
} from "../../../../../../../../api/types/svTypes.ts";

import { isTabularPageCancellation } from "./isTabularPageCancellation.ts";

const PAGE_SIZE = 500;
const MAX_CACHE_ENTRIES = 32;

interface SnapshotDataSource {
  dataSource: GridDataSource;
  cache: GridDataSourceCache;
  activate: () => void;
  dispose: () => void;
}

export interface SnapshotRange {
  parentIndex?: number;
  skip: number;
  take: number;
}

export const getSnapshotRange = (params: Pick<GridGetRowsParams, "start" | "end" | "groupKeys">): SnapshotRange => {
  const skip = Number(params.start ?? 0);
  const take = params.end === undefined ? PAGE_SIZE : params.end - skip + 1;
  const key = params.groupKeys?.at(-1);
  const parentIndex = key === undefined ? undefined : Number(key);
  if (
    !Number.isSafeInteger(skip) ||
    skip < 0 ||
    !Number.isSafeInteger(take) ||
    take < 1 ||
    (parentIndex !== undefined && (!Number.isSafeInteger(parentIndex) || parentIndex < 0))
  ) {
    throw new Error("Invalid report page range.");
  }
  return { skip, take, parentIndex };
};

export const createTabularSnapshotDataSource = (
  snapshot: TabularSnapshot,
  firstPage: TabularRow[],
  grandTotals: Record<string, unknown>,
  fetchPage: (
    request: TabularSnapshotPageRequest,
    dataSetId: string | undefined,
    signal: AbortSignal
  ) => Promise<ApiResponse<TabularDataResponse>>,
  onPageErrorChanged?: (message: string | undefined) => void,
  onResultExpired?: () => boolean
): SnapshotDataSource => {
  let controller = new AbortController();
  let expired = false;
  let recoveryRequested = false;
  const rejectExpiredResult = (): never => {
    if (!expired) {
      expired = true;
      recoveryRequested = onResultExpired?.() ?? false;
    }
    if (recoveryRequested) {
      throw new DOMException("Report refresh requested.", "AbortError");
    }
    throw new Error("Unable to refresh this report. Please try again.");
  };
  const entries = new Map<string, GridGetRowsResponse>();
  const cacheKey = (params: GridGetRowsParams): string => JSON.stringify(params);
  const cache: GridDataSourceCache = {
    get: (params) => {
      if (controller.signal.aborted || Date.now() >= Date.parse(snapshot.expiresAt)) {
        entries.clear();
        return undefined;
      }
      return entries.get(cacheKey(params));
    },
    set: (params, response) => {
      const key = cacheKey(params);
      entries.delete(key);
      entries.set(key, response);
      while (entries.size > MAX_CACHE_ENTRIES) {
        const oldest = entries.keys().next().value;
        if (oldest === undefined) {
          break;
        }
        entries.delete(oldest);
      }
    },
    clear: () => entries.clear(),
  };

  const failedRanges = new Map<string, string>();
  const loadRows = async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
    const signal = controller.signal;
    signal.throwIfAborted();
    if (expired || Date.now() >= Date.parse(snapshot.expiresAt)) {
      rejectExpiredResult();
    }
    const range = getSnapshotRange(params);
    if (
      range.parentIndex === undefined &&
      range.skip === 0 &&
      (range.take <= firstPage.length || firstPage.length === snapshot.rootCount)
    ) {
      return {
        rows: firstPage.slice(0, range.take),
        rowCount: snapshot.rootCount,
        aggregateRow: { totals: grandTotals },
      };
    }
    const rows: TabularRow[] = [];
    let rowCount: number | undefined;
    for (let skip = range.skip; skip < range.skip + range.take; skip += PAGE_SIZE) {
      const response = await fetchPage(
        {
          resultId: snapshot.resultId,
          parentIndex: range.parentIndex,
          skip,
          take: Math.min(PAGE_SIZE, range.skip + range.take - skip),
        },
        snapshot.dataSetId,
        signal
      );
      signal.throwIfAborted();
      if (expired || response.data?.errorCode === "ReportResultExpired") {
        rejectExpiredResult();
      }
      if (!response.success || response.data.errorCode) {
        throw new Error(response.error?.message ?? "Unable to load this report. Please try again.");
      }
      rowCount = response.data.totalCount;
      rows.push(...response.data.data);
      if (rowCount !== undefined && skip + response.data.data.length >= rowCount) {
        break;
      }
      if (response.data.data.length === 0) {
        throw new Error("The report page is incomplete. Run the report again to refresh it.");
      }
    }
    return { rows, rowCount, aggregateRow: { totals: grandTotals } };
  };
  const dataSource: GridDataSource = {
    getRows: async (params) => {
      const signal = controller.signal;
      const key = JSON.stringify(getSnapshotRange(params));
      try {
        const result = await loadRows(params);
        signal.throwIfAborted();
        if (failedRanges.delete(key)) {
          onPageErrorChanged?.(failedRanges.values().next().value);
        }
        return result;
      } catch (error) {
        if (!signal.aborted && !isTabularPageCancellation(error)) {
          const message = error instanceof Error ? error.message : "Unable to load report page.";
          failedRanges.set(key, message);
          onPageErrorChanged?.(message);
        }
        throw error;
      }
    },
    getGroupKey: (row) => String(row["index"]),
    getChildrenCount: (row) => (typeof row["childrenCount"] === "number" ? row["childrenCount"] : 0),
    getAggregatedValue: (row, field) => {
      const value: unknown = row["totals"]?.[field]?.formattedValue;
      return typeof value === "string" ? value : "";
    },
  };
  return {
    dataSource,
    cache,
    activate: () => {
      if (controller.signal.aborted) {
        controller = new AbortController();
      }
    },
    dispose: () => {
      controller.abort();
      entries.clear();
    },
  };
};
